import { useForm } from "@refinedev/antd";
import { HttpError, useApiUrl, useCustom } from "@refinedev/core";
import { config } from "@src/config";
import { FORM_NAME } from "@src/constant/ab-testing/user-count";
import { ChartFormFieldsEnum, FilterFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { Form } from "antd";
import { FormInstance } from "antd/lib/form";
import { Dayjs } from "dayjs";
import React from "react";
import { createContext, useContext } from "react";
import { NavigateFunction } from "react-router-dom";

export type FilterFormInstanceType = {
    [FilterFormFieldsEnum.TITLE]?: string;
    [FilterFormFieldsEnum.DESCRIPTION]?: string;
    [FilterFormFieldsEnum.ABA_ID]: string;
    [FilterFormFieldsEnum.AMA_APP_IDS]: string[];
    [FilterFormFieldsEnum.IS_ABA_ID_CHANGED]: boolean;
    [FilterFormFieldsEnum.IS_SHOW_CHIP]: boolean;
    [FilterFormFieldsEnum.IS_FIRST_APPLIED]: boolean;
    [FilterFormFieldsEnum.PLATFORM]: string[];
    [FilterFormFieldsEnum.PRODUCT_CODE]: string;
    [FilterFormFieldsEnum.PMP_PRODUCT_ID]: number;
    [FilterFormFieldsEnum.VARIANTS]: string[];
    [FilterFormFieldsEnum.BASE_VARIANTS]: string;
    [FilterFormFieldsEnum.TICKET_ID]: number;
    [FilterFormFieldsEnum.TIME_RANGE]: Dayjs[];
    [FilterFormFieldsEnum.VARIANTS_OPTIONS]: { label: string | undefined; value: string }[];
    [FilterFormFieldsEnum.COHORT_DATE]: string;
    [FilterFormFieldsEnum.MAX_DAY_DIFF]: number;

    //extra fields
    [FilterFormFieldsEnum.EXTRA_INSTALL_DATE]: Dayjs[] | undefined;
    [FilterFormFieldsEnum.EXTRA_ONBOARDING_DATE]: Dayjs[] | undefined;

    [FilterFormFieldsEnum.INVALID_COUNTRIES]: boolean;
    [FilterFormFieldsEnum.USERS_JOIN_MULTIPLE_GROUPS]: boolean;
    [FilterFormFieldsEnum.MULTI_USER_PSEUDO_ID]: boolean;
    [FilterFormFieldsEnum.LATE_ONBOARDING_DATE]: boolean;

    [FilterFormFieldsEnum.EXTRA_COUNTRY]: string[];
    [FilterFormFieldsEnum.EXTRA_MEDIA_SOURCE]: string[];
    [FilterFormFieldsEnum.EXTRA_DEVICE_MODAL]: string[];
    [FilterFormFieldsEnum.EXTRA_DEVICE_CATEGORY]: string[];
    [FilterFormFieldsEnum.EXTRA_FIRST_APP_VERSION]: string[];
    [FilterFormFieldsEnum.EXTRA_LAST_APP_VERSION]: string[];
    [FilterFormFieldsEnum.EXTRA_FILTER_VALUES_CONFIRMED]: string;
};

export type ChartFormInstanceType = {
    [ChartFormFieldsEnum.FILTER_FIELDS]: string;
    [ChartFormFieldsEnum.MAX_DAY_DIFF]: number;

    [ChartFormFieldsEnum.AD_NETWORK]: string[];
    [ChartFormFieldsEnum.AD_UNITS]: string[];
    [ChartFormFieldsEnum.PACKAGE]: string[];

    [ChartFormFieldsEnum.END_DATE]: string | undefined;
    [ChartFormFieldsEnum.START_DATE]: string | undefined;

    [ChartFormFieldsEnum.EVENT_NAME]: string;
    [ChartFormFieldsEnum.EVENT_PARAM]: string;
    [ChartFormFieldsEnum.EVENT_PARAM_OPTIONS]: any[];
    [ChartFormFieldsEnum.PARAM_VALUE]: string[];
    [ChartFormFieldsEnum.IS_ALLOW_GET_USER_EVENT_ENGAGEMENT]: boolean;

    [ChartFormFieldsEnum.METRIC_SIGNIFICANCE_LEVEL]: number | undefined;
    [ChartFormFieldsEnum.METRIC_TAB_ITEMS]: any[];
} & Record<string, any>;

type ABContextType = {
    filterForm: FormInstance<FilterFormInstanceType>;
    chartForm: FormInstance<ChartFormInstanceType>;
    updateSearchUrl: (
        initData?: {
            filterForm?: FilterFormInstanceType;
            chartForm?: ChartFormInstanceType;
        },
        navigate?: NavigateFunction,
    ) => void;
    getCurrentConfig: () => any;
    getAndFormatFilterForm: (e: FilterFormInstanceType) => any;
    getAndFormatChartForm: (e: ChartFormInstanceType) => any;
    listEventNames: any[];
    isLoadingEventName: boolean;
};
export const ABContext = createContext({} as ABContextType);

type IProps = {
    children: React.ReactNode;
};

export const ABContextProvider: React.FC<IProps> = ({ children }) => {
    const { form: filterForm } = useForm<any, HttpError, FilterFormInstanceType>();
    const { form: chartForm } = useForm<any, HttpError, ChartFormInstanceType>();

    const getAndFormatFilterForm = (e: FilterFormInstanceType) => {
        const result: any = {};
        [
            FilterFormFieldsEnum.ABA_ID,
            FilterFormFieldsEnum.AMA_APP_IDS,
            FilterFormFieldsEnum.PLATFORM,
            FilterFormFieldsEnum.PRODUCT_CODE,
            FilterFormFieldsEnum.PMP_PRODUCT_ID,
            FilterFormFieldsEnum.VARIANTS,
            FilterFormFieldsEnum.BASE_VARIANTS,
            FilterFormFieldsEnum.TICKET_ID,
            FilterFormFieldsEnum.VARIANTS_OPTIONS,
            FilterFormFieldsEnum.TIME_RANGE,
            FilterFormFieldsEnum.COHORT_DATE,
            FilterFormFieldsEnum.MAX_DAY_DIFF,
            FilterFormFieldsEnum.EXTRA_FILTER_VALUES_CONFIRMED,
        ].forEach((key) => {
            switch (key) {
                case FilterFormFieldsEnum.TIME_RANGE:
                    result[key] = e[key]?.map((_v) => _v.format(config.DATE_FORMAT));
                    break;
                case FilterFormFieldsEnum.VARIANTS: {
                    result[key] = e[key]?.sort((a, b) => String(a).localeCompare(String(b)));
                    return;
                }
                case FilterFormFieldsEnum.VARIANTS_OPTIONS:
                    result[key] = e[key]?.sort((a, b) => a.value.localeCompare(b.value));
                    break;
                case FilterFormFieldsEnum.EXTRA_FILTER_VALUES_CONFIRMED: {
                    const values = JSON.parse(e[key] ?? "{}");

                    Object.keys(values)?.forEach((field) => {
                        result[field] = values[field];
                    });
                    break;
                }
                default: {
                    result[key] = e[key];
                }
            }
        });

        return result;
    };
    const getAndFormatChartForm = (e: ChartFormInstanceType) => {
        const result: any = {};
        [
            ChartFormFieldsEnum.METRIC_TAB_ITEMS,
            ChartFormFieldsEnum.FILTER_FIELDS,
            ChartFormFieldsEnum.FUNNEL_BREAKDOWN,
            ChartFormFieldsEnum.FUNNEL_BREAKDOWN_VALUE,
            ChartFormFieldsEnum.FUNNEL_SIGNIFICANT,
            ChartFormFieldsEnum.FUNNEL_EVENT_LISTS,
            ChartFormFieldsEnum.FUNNEL_EVENT_OPTIONS_ADDITION,
            ChartFormFieldsEnum.FUNNEL_FILTER,
            ChartFormFieldsEnum.RETENTION_BREAKDOWN,
            ChartFormFieldsEnum.RETENTION_BREAKDOWN_VALUE,
            ChartFormFieldsEnum.RETENTION_SIGNIFICANT,
        ].forEach((key) => {
            result[key] = e[key as ChartFormFieldsEnum];
            (result[ChartFormFieldsEnum.METRIC_TAB_ITEMS] ?? [])?.forEach((item: any) => {
                if (item.key) {
                    delete result[item.key];
                }
            });
        });

        return result;
    };

    const getCurrentConfig = () => {
        return {
            [FORM_NAME.FILTER_FORM]: getAndFormatFilterForm(filterForm.getFieldsValue()),
            [FORM_NAME.CHART_FORM]: getAndFormatChartForm(chartForm.getFieldsValue()),
        };
    };

    const productCodeWatch = Form.useWatch(FilterFormFieldsEnum.PRODUCT_CODE, filterForm);

    const apiUrl = useApiUrl("nestjsx");
    const { data, isLoading } = useCustom({
        method: "get",
        dataProviderName: "nestjsx",
        url: `${apiUrl}/products/event-name/${productCodeWatch}`,
        queryOptions: {
            enabled: Boolean(productCodeWatch),
        },
        config: {
            headers: {
                "Product-Code": productCodeWatch,
            },
        },
    });
    const updateSearchUrl = (
        initData?: {
            filterForm?: FilterFormInstanceType;
            chartForm?: ChartFormInstanceType;
        },
        navigate?: NavigateFunction,
    ) => {
        return;
    };

    const values = {
        filterForm,
        chartForm,
        updateSearchUrl,
        getCurrentConfig,
        getAndFormatFilterForm,
        getAndFormatChartForm,
        listEventNames: (data?.data ?? []) as any[],
        isLoadingEventName: isLoading,
    };
    return <ABContext.Provider value={values}>{children}</ABContext.Provider>;
};

export const useABContext = () => useContext(ABContext);
